/**
 * ShareTrackingWidget - Dashboard widget showing product share analytics
 * Displays share counts by channel with visual charts
 */
import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Share2,
  RefreshCw,
  Loader2,
  MessageCircle,
  Facebook,
  Twitter,
  Mail,
  Link,
  Smartphone,
  TrendingUp,
  ExternalLink
} from 'lucide-react';

// Channel icons and colors
const CHANNEL_CONFIG = {
  native: { 
    icon: Smartphone, 
    label: 'Native Share', 
    color: '#10b981',
    bg: 'bg-emerald-500/20'
  },
  whatsapp: { 
    icon: MessageCircle, 
    label: 'WhatsApp', 
    color: '#25D366',
    bg: 'bg-green-500/20'
  },
  facebook: { 
    icon: Facebook, 
    label: 'Facebook', 
    color: '#1877F2',
    bg: 'bg-blue-500/20'
  },
  twitter: { 
    icon: Twitter, 
    label: 'X (Twitter)', 
    color: '#1DA1F2',
    bg: 'bg-sky-500/20'
  },
  email: { 
    icon: Mail, 
    label: 'Email', 
    color: '#f59e0b',
    bg: 'bg-amber-500/20'
  },
  copy: { 
    icon: Link, 
    label: 'Copy Link', 
    color: '#8b5cf6',
    bg: 'bg-purple-500/20'
  }
};

export default function ShareTrackingWidget({ expanded = false }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/metrics/share/stats?days=${period}`);
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching share stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [period]);

  const getChannelData = () => {
    if (!stats?.by_channel) return [];
    
    return Object.entries(stats.by_channel)
      .map(([channel, count]) => ({
        channel,
        count,
        config: CHANNEL_CONFIG[channel] || { 
          icon: Share2, 
          label: channel, 
          color: '#6b7280',
          bg: 'bg-gray-500/20'
        }
      }))
      .sort((a, b) => b.count - a.count);
  };

  const channelData = getChannelData();
  const maxCount = Math.max(...channelData.map(d => d.count), 1);

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-2xl p-4 border border-gray-700">
        <div className="flex items-center gap-2 mb-3">
          <Share2 className="h-5 w-5 text-pink-400" />
          <h3 className="font-semibold">Share Analytics</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-2xl p-4 border border-gray-700 hover:border-gray-600 transition-all">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-pink-500/20">
            <Share2 className="h-5 w-5 text-pink-400" />
          </div>
          <h3 className="font-semibold">Share Analytics</h3>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="bg-gray-700 border-0 rounded-lg px-2 py-1 text-sm text-gray-300 focus:ring-1 focus:ring-pink-500"
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
          <Button
            variant="ghost"
            size="icon"
            onClick={fetchStats}
            className="h-8 w-8 text-gray-400 hover:text-white"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {stats && (
        <div className="space-y-4">
          {/* Total Shares */}
          <div className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Shares</p>
                <p className="text-3xl font-bold text-white">{stats.total_shares.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-full bg-pink-500/30">
                <TrendingUp className="h-6 w-6 text-pink-400" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Last {period} days</p>
          </div>

          {/* Channel Breakdown */}
          {channelData.length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-400 font-medium">By Channel</p>
              {channelData.map(({ channel, count, config }) => {
                const Icon = config.icon;
                const percentage = (count / maxCount) * 100;
                
                return (
                  <div key={channel} className="group">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-lg ${config.bg}`}>
                          <Icon className="h-3.5 w-3.5" style={{ color: config.color }} />
                        </div>
                        <span className="text-sm text-gray-300">{config.label}</span>
                      </div>
                      <span className="text-sm font-semibold text-white">{count}</span>
                    </div>
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500 group-hover:opacity-80"
                        style={{ 
                          width: `${percentage}%`,
                          backgroundColor: config.color
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <Share2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No shares recorded yet</p>
              <p className="text-xs mt-1">Shares will appear here when products are shared</p>
            </div>
          )}

          {/* Quick Stats */}
          {channelData.length > 0 && (
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-700">
              <div className="text-center p-2">
                <p className="text-xs text-gray-500">Top Channel</p>
                <p className="text-sm font-medium text-white">
                  {channelData[0]?.config.label || '-'}
                </p>
              </div>
              <div className="text-center p-2">
                <p className="text-xs text-gray-500">Avg/Day</p>
                <p className="text-sm font-medium text-white">
                  {(stats.total_shares / period).toFixed(1)}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
